import React from 'react';
import { 
  HeartIcon, 
  HomeIcon, 
  UsersIcon, 
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import useSEO from '../hooks/useSEO';
import { CONTACT_INFO } from '../constants/contact';

// Team members data
const teamMembers = [
  {
    id: 1,
    name: "Kuber Dhakal",
    position: "Owner & Ward Chairman",
    image: "/images/about/owner.png",
    description: "With 5 years of international experience in South Korea and deep local knowledge as Ward Chairman of Bethanchok-03, Kuber brings a unique blend of global perspective and intimate village understanding to create exceptional homestay experiences."
  },
  {
    id: 2,
    name: "Kriyans Dhakal",
    position: "Managing Director",
    image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80",
    description: "Leading the day-to-day operations of the homestay, Kriyans ensures every guest receives exceptional service and memorable experiences."
  },
  {
    id: 3,
    name: "Sushila Sharma",
    position: "Coordinator",
    image: "/images/about/coordinator.png",
    description: "Sushila coordinates all guest activities and ensures smooth communication between guests and staff, making sure every need is met."
  },
  {
    id: 4,
    name: "Sapana Lama",
    position: "Housekeeper",
    image: "/images/about/housekeeper.png",
    description: "Sapana maintains the cleanliness and comfort of our homestay, ensuring every room is welcoming and spotless for our guests."
  },
  {
    id: 5,
    name: "Sagar Dhakal",
    position: "Marketing Manager",
    image: "/images/about/marketing.png",
    description: "Sagar manages our marketing efforts and guest relations, helping spread the word about our authentic homestay experience and ensuring we reach travelers seeking genuine Nepali hospitality."
  }
];

export default function About() {
  const aboutStructuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "LodgingBusiness",
      "name": "Bethanchok Homestay",
      "description": "Family-owned homestay in Bethanchok village, Nepal, offering authentic cultural experiences and traditional Nepali hospitality",
      "foundingDate": "2020",
      "founder": {
        "@type": "Person",
        "name": "Kuber Dhakal",
        "jobTitle": "Owner & Ward Chairman",
        "description": "Experienced hospitality professional with 5 years international experience and deep local knowledge"
      }
    }  };

  // SEO Hook
  useSEO({
    title: "About Bethanchok Homestay | Our Story & Team | Authentic Nepal Experience",
    description: "Learn about Bethanchok Homestay - a family-owned business in Nepal offering authentic cultural experiences. Meet our dedicated team led by owner Kuber Dhakal, Ward Chairman with international experience and deep local knowledge.",
    keywords: "About Bethanchok Homestay, Nepal homestay owner, Kuber Dhakal, Bethanchok village story, authentic Nepal hospitality, Nepal homestay team, traditional Nepal accommodation story, Nepal cultural tourism, Ward Chairman Bethanchok",
    image: "https://bethanchokstay.com/images/about/owner.png",
    url: "https://bethanchokstay.com/about",
    structuredData: aboutStructuredData
  });

  return (
    <div className="bg-white page-transition">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
              About Us
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-100">
              Discover our story, meet our family, and learn about our commitment to providing 
              authentic Nepali hospitality in the heart of Bethanchok village.
            </p>
          </div>
        </div>
      </div>

      {/* Owner's Message */}      <div className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-3xl p-8 lg:p-12"><div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">                <div className="flex-shrink-0">
                  <div className="w-64 h-80 lg:w-72 lg:h-96 rounded-2xl bg-primary-200 flex items-center justify-center overflow-hidden">
                    <img
                      src="/images/about/owner.png"
                      alt="Kuber Dhakal - Owner"
                      className="w-full h-full object-cover shadow-lg"
                    />
                  </div>
                </div>                <div className="flex-1 text-center lg:text-left">
                  <h2 className="text-2xl lg:text-3xl font-bold text-neutral-900 mb-4">
                    A Message from Our Owner
                  </h2>                  <p className="text-base text-neutral-700 leading-relaxed mb-6">
                    "Namaste and warm greetings from our family to yours! Welcome to Bethanchok Homestay, 
                    where we've been sharing the beauty of Nepali culture and hospitality for over a decade. 
                    Our home is your home, and we're honored to be part of your journey through Nepal."
                  </p>
                  <p className="text-base text-neutral-700 leading-relaxed mb-6">
                    "My journey has taken me far from home – I spent five transformative years working in South Korea, 
                    where I gained invaluable international experience and learned to appreciate different cultures. 
                    This experience has deeply enriched my understanding of hospitality and helped me create a 
                    homestay that bridges cultures and makes international guests feel truly welcome."
                  </p>
                  <p className="text-base text-neutral-700 leading-relaxed mb-6">
                    "Today, as the Ward Chairman of Bethanchok-03, I have the privilege of serving our beautiful 
                    village community. This role has given me an intimate understanding of our local traditions, 
                    the needs of our people, and the incredible stories that make Bethanchok special. I know 
                    every corner of our village, every family's history, and the best hidden gems that most 
                    visitors never discover."
                  </p>
                  <p className="text-base text-neutral-700 leading-relaxed mb-6">
                    "We believe that true travel happens when you connect with local people and experience 
                    life as we do. Here at Bethanchok, you're not just a guest – you're family. I'm excited 
                    to share our traditions, our food, our stories, and guide you through the authentic heart 
                    of Nepal that only a local leader can reveal."
                  </p>
                  <div className="flex items-center justify-center lg:justify-start">
                    <div>
                      <p className="font-semibold text-neutral-900">Kuber Dhakal</p>
                      <p className="text-sm text-neutral-600">Owner, Bethanchok Homestay</p>
                      <p className="text-sm text-neutral-500">Ward Chairman, Bethanchok-03</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About Us Details */}
      <div className="py-16 bg-neutral-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
              Our Story
            </h2>
            <p className="mt-4 text-lg leading-8 text-neutral-600">
              Learn about our heritage, our values, and what makes Bethanchok Homestay special.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-neutral-900 mb-6">
                Our Heritage & Values
              </h3>              <div className="space-y-6">
                <p className="text-neutral-700 leading-relaxed">
                  Bethanchok Homestay was founded by Kuber Dhakal, whose unique journey combines 
                  international experience with deep local roots. After spending five years working 
                  in South Korea, Kuber returned to his beloved village with a global perspective 
                  and renewed appreciation for Nepali culture and hospitality.
                </p>
                <p className="text-neutral-700 leading-relaxed">
                  As the elected Ward Chairman of Bethanchok-03, Kuber possesses an unparalleled 
                  understanding of our village's history, traditions, and community needs. His 
                  leadership role has given him intimate knowledge of every family, every story, 
                  and every hidden treasure that makes Bethanchok special.
                </p>
                <p className="text-neutral-700 leading-relaxed">
                  Located in the heart of Bethanchok village, our homestay offers an authentic 
                  window into traditional Nepali life. We're committed to sustainable tourism 
                  practices that benefit our local community while providing our guests with 
                  unforgettable experiences guided by genuine local expertise.
                </p>
                <p className="text-neutral-700 leading-relaxed">
                  Over the years, we've welcomed hundreds of guests from around the world, each 
                  becoming part of our extended family. We take pride in our traditional Nepali 
                  architecture, organic farming practices, and our dedication to preserving the 
                  natural beauty of our surroundings while sharing authentic village life.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1544735716-392fe2489ffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Traditional Nepali home"
                className="rounded-2xl shadow-lg w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
            </div>
          </div>        </div>
      </div>

      {/* Family & Team */}
      <div className="py-16 bg-neutral-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
              Meet Our Team
            </h2>
            <p className="mt-4 text-lg leading-8 text-neutral-600">
              Get to know the wonderful people who will make your stay memorable.
            </p>          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {teamMembers.slice(0, 3).map((member) => (
              <div key={member.id} className="text-center">
                <div className="relative mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-40 h-40 rounded-full object-cover mx-auto shadow-lg"
                  />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-2">{member.name}</h3>
                <p className="text-primary-600 font-medium mb-3">{member.position}</p>
                <p className="text-neutral-600 text-sm leading-relaxed">
                  {member.description}
                </p>
              </div>
            ))}
          </div>          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
            {teamMembers.slice(3).map((member) => (
              <div key={member.id} className="text-center">
                <div className="relative mb-6">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-40 h-40 rounded-full object-cover mx-auto shadow-lg"
                  />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-2">{member.name}</h3>
                <p className="text-primary-600 font-medium mb-3">{member.position}</p>
                <p className="text-neutral-600 text-sm leading-relaxed">
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-3xl p-8 lg:p-12">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-8">
                Get in Touch
              </h2>
              <p className="text-lg leading-8 text-primary-100 mb-8">
                Ready to experience authentic Nepali hospitality? We'd love to welcome you to our family.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div className="flex items-center space-x-4 text-white">
                  <PhoneIcon className="h-6 w-6 text-primary-200" />
                  <div>
                    <p className="font-semibold">Phone</p>
                    <p className="text-primary-100">{CONTACT_INFO.phone}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-white">
                  <EnvelopeIcon className="h-6 w-6 text-primary-200" />
                  <div>
                    <p className="font-semibold">Email</p>
                    <p className="text-primary-100">{CONTACT_INFO.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-white md:col-span-2">
                  <MapPinIcon className="h-6 w-6 text-primary-200" />
                  <div>
                    <p className="font-semibold">Address</p>
                    <p className="text-primary-100">{CONTACT_INFO.address.fullAddress}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}